import React, { useState, useEffect, useCallback } from 'react';
import { Bookmark, Check, UserPlus, UserCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const COPY = {
  watchlist: { off: 'Watchlist', on: 'On watchlist', OffIcon: Bookmark, OnIcon: Check },
  follow: { off: 'Follow', on: 'Following', OffIcon: UserPlus, OnIcon: UserCheck },
};

// Add / remove a graph entity (title, person, franchise, studio, service) to
// the user's watchlist/follow graph — the same relationship, `verb` only
// changes the label (titles read as "watchlist", people/studios/franchises
// read as "follow"). Self-contained: checks the current state on mount and
// toggles on click.
//
// Franchise/studio detail pages are graph-native and already have the real
// `entityId`. A person page only has a TMDb id (PersonDetail.js has no graph
// `people.id`), so for entityType="person" pass `tmdbId` + `name` instead —
// the backend resolves/creates the graph row (idempotently) on first follow,
// and the resolved id is cached locally so a later unfollow targets it.
export default function WatchlistButton({
  entityType, entityId = null, tmdbId = null, name = null, verb = 'watchlist', apiBase = DEFAULT_API,
}) {
  const { token } = useAuth();
  const [following, setFollowing] = useState(false);
  const [resolvedId, setResolvedId] = useState(entityId);
  const [busy, setBusy] = useState(false);
  const copy = COPY[verb] || COPY.watchlist;

  const authHeaders = useCallback(
    () => (token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }),
    [token]
  );

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/api/watchlist`, { headers: authHeaders() });
        if (!r.ok) return;
        const { follows } = await r.json();
        const match = (follows || []).find((f) => f.entity_type === entityType
          && (entityId ? f.entity_id === entityId : f.tmdb_id === tmdbId));
        if (active && match) { setFollowing(true); setResolvedId(match.entity_id); }
      } catch { /* leave as not-following */ }
    })();
    return () => { active = false; };
  }, [apiBase, entityId, entityType, tmdbId, authHeaders]);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    const next = !following;
    try {
      if (next) {
        const body = entityId ? { entity_type: entityType, entity_id: entityId }
                               : { entity_type: entityType, tmdb_id: tmdbId, name };
        const resp = await fetch(`${apiBase}/api/watchlist`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
        if (resp.ok) { const data = await resp.json(); setResolvedId(data.entity_id); setFollowing(true); }
      } else if (resolvedId) {
        const resp = await fetch(`${apiBase}/api/watchlist/${entityType}/${resolvedId}`, { method: 'DELETE', headers: authHeaders() });
        if (resp.ok) setFollowing(false);
      }
    } catch { /* keep current state on failure */ } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
        following
          ? 'bg-videbits-100 text-videbits-700 hover:bg-videbits-200'
          : 'bg-videbits-600 text-white hover:bg-videbits-700'
      }`}
    >
      {following ? <><copy.OnIcon className="w-4 h-4" /> {copy.on}</> : <><copy.OffIcon className="w-4 h-4" /> {copy.off}</>}
    </button>
  );
}
